/**
 * Nominatim (OpenStreetMap) geocoding, restricted to Bucharest.
 *
 * Used only for the address autocomplete on step 1. It never confirms anything;
 * a picked suggestion stores its formatted label and coordinates, and the owner
 * can always type the address by hand instead.
 *
 * Usage policy compliance — https://operations.osmfoundation.org/policies/nominatim/
 *  - Absolute maximum of 1 request/second. `throttle()` below is a hard serial
 *    gate at module scope, so every caller shares one queue. The UI debounce is
 *    a separate, softer guard; this is the one that keeps us within policy even
 *    if a component misbehaves.
 *  - The policy asks for an identifying User-Agent. Browsers forbid setting that
 *    header, so we identify via the `email` parameter (which Nominatim documents
 *    for exactly this purpose) and the Referer the browser sends automatically.
 *  - Results are cached per query for the page session to avoid repeat calls.
 */

const BASE_URL = (import.meta.env.VITE_NOMINATIM_URL || 'https://nominatim.openstreetmap.org').replace(
  /\/$/,
  '',
);
const CONTACT_EMAIL = import.meta.env.VITE_NOMINATIM_EMAIL || '';

/**
 * Bucharest bounding box, as two opposite corners: [west, north, east, south].
 * Passed to Nominatim with `bounded=1`, so results outside the city are dropped
 * rather than merely deprioritised.
 */
const BUCHAREST_VIEWBOX = '25.95,44.55,26.26,44.33';

const MIN_INTERVAL_MS = 1100; // 1 req/sec, plus headroom for clock skew.

let lastRequestAt = 0;
let chain: Promise<unknown> = Promise.resolve();

/** Serialises all callers and spaces them at least MIN_INTERVAL_MS apart. */
function throttle<T>(task: () => Promise<T>): Promise<T> {
  const run = chain.then(async () => {
    const wait = Math.max(0, lastRequestAt + MIN_INTERVAL_MS - Date.now());
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    lastRequestAt = Date.now();
    return task();
  });
  // Keep the chain alive even if this task rejects, or one failure stalls the queue.
  chain = run.catch(() => undefined);
  return run;
}

export interface GeocodeResult {
  id: string;
  label: string;
  lat: number;
  lon: number;
}

interface NominatimPlace {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

function buildUrl(path: string, params: Record<string, string>): string {
  const url = new URL(`${BASE_URL}${path}`);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('countrycodes', 'ro');
  url.searchParams.set('accept-language', 'ro');
  // Confine results to Bucharest.
  url.searchParams.set('viewbox', BUCHAREST_VIEWBOX);
  url.searchParams.set('bounded', '1');
  for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
  if (CONTACT_EMAIL) url.searchParams.set('email', CONTACT_EMAIL);
  return url.toString();
}

const searchCache = new Map<string, GeocodeResult[]>();

/**
 * Forward-geocode an address within Bucharest.
 * Rejects with the AbortError if `signal` fires, so callers can drop stale queries.
 */
/**
 * Adevărat când ULTIMA căutare a eșuat pe serviciu (nu pe abort). Validarea pasului 1
 * cere alegerea unei sugestii; excepția e exact acest caz — geocoderul picat nu are
 * voie să blocheze trimiterea formularului (am pierde lead-uri la o pană externă).
 */
let serviceDown = false;
export const geocoderUnavailable = (): boolean => serviceDown;

/**
 * Photon (komoot) — geocoder pe aceleași date OpenStreetMap, dar cu căutare FUZZY:
 * tolerează greșeli de scriere („Strafa Joseph Lister", „Listter") pe care Nominatim
 * le respinge sec. E motorul PRINCIPAL al sugestiilor; Nominatim rămâne rezerva
 * când Photon nu răspunde. bbox = aceeași cutie a Bucureștiului (vest,sud,est,nord).
 */
const PHOTON_URL = 'https://photon.komoot.io/api';
const PHOTON_BBOX = '25.95,44.33,26.26,44.55';

interface PhotonFeature {
  properties: {
    osm_id?: number;
    osm_type?: string;
    name?: string;
    street?: string;
    housenumber?: string;
    district?: string;
    city?: string;
  };
  geometry: { coordinates: [number, number] };
}

function photonToResult(f: PhotonFeature, index: number): GeocodeResult | null {
  const p = f.properties;
  const primary = p.name ?? (p.street ? `${p.street}${p.housenumber ? ` ${p.housenumber}` : ''}` : null);
  if (!primary) return null;
  const secondary = p.district ?? p.city ?? 'București';
  const [lon, lat] = f.geometry.coordinates;
  return {
    id: p.osm_id != null ? `${p.osm_type ?? ''}${p.osm_id}` : `photon-${index}`,
    label: `${primary}, ${secondary}`,
    lat,
    lon,
  };
}

async function searchPhoton(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const url = new URL(PHOTON_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('bbox', PHOTON_BBOX);
  url.searchParams.set('limit', '10');
  return throttle(async () => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(url.toString(), { signal: signal ?? null, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Photon search failed: ${response.status}`);
    const data = (await response.json()) as { features?: PhotonFeature[] };
    // Photon întoarce adesea segmente multiple ale aceleiași străzi → dedup pe etichetă.
    const seen = new Set<string>();
    const out: GeocodeResult[] = [];
    for (const [i, f] of (data.features ?? []).entries()) {
      const r = photonToResult(f, i);
      if (!r || seen.has(r.label)) continue;
      seen.add(r.label);
      out.push(r);
      if (out.length >= 6) break;
    }
    return out;
  });
}

async function searchNominatim(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const url = buildUrl('/search', { q: query, limit: '6', addressdetails: '0' });
  return throttle(async () => {
    // Re-check after the queue wait: the user may have typed on since we queued.
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(url, { signal: signal ?? null, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Nominatim search failed: ${response.status}`);
    const places = (await response.json()) as NominatimPlace[];
    return places.map(toGeocodeResult);
  });
}

const isAbort = (err: unknown): boolean => err instanceof DOMException && err.name === 'AbortError';

export async function searchPlaces(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];

  const cacheKey = trimmed.toLowerCase();
  const cached = searchCache.get(cacheKey);
  if (cached) return cached;

  let results: GeocodeResult[];
  try {
    results = await searchPhoton(trimmed, signal);
  } catch (photonErr) {
    if (isAbort(photonErr)) throw photonErr; // interogare veche — nu e pană
    try {
      results = await searchNominatim(trimmed, signal);
    } catch (nominatimErr) {
      if (!isAbort(nominatimErr)) serviceDown = true; // ambele picate → fallback-ul manual se deschide
      throw nominatimErr;
    }
  }

  serviceDown = false;
  searchCache.set(cacheKey, results);
  return results;
}

function toGeocodeResult(place: NominatimPlace): GeocodeResult {
  return {
    id: String(place.place_id),
    label: place.display_name,
    lat: Number.parseFloat(place.lat),
    lon: Number.parseFloat(place.lon),
  };
}

/** Shorten Nominatim's long display_name to something readable in a result list. */
export function shortLabel(label: string): string {
  const parts = label.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 3) return parts.join(', ');
  // Keep the most specific parts; drop the country/postcode tail.
  return parts.slice(0, 3).join(', ');
}

/** The house number the owner typed, if any: an explicit "nr. 11" wins, else the trailing number. */
function extractHouseNumber(typed: string): string | null {
  const explicit = typed.match(/\b(?:nr|no)\.?\s*(\d{1,4}[a-zA-Z]?)\b/i);
  if (explicit) return explicit[1] ?? null;
  // House numbers usually trail the street name ("Strada X 11"); take the last.
  const all = [...typed.matchAll(/\b(\d{1,4}[a-zA-Z]?)\b/g)];
  const last = all[all.length - 1];
  return last ? (last[1] ?? null) : null;
}

/**
 * Keep the house number when a street-level suggestion is chosen.
 *
 * Nominatim rarely has Bucharest house numbers, so picking a suggestion for
 * "Strada Teiul Doamnei nr. 11" returns only "Strada Teiul Doamnei, Sector 2"
 * and would drop the 11 — but the number is the apartment's identity. This
 * injects the typed number into the street segment of the canonical label, so
 * the result is "Strada Teiul Doamnei nr. 11, Sector 2" rather than losing it.
 */
export function mergeHouseNumber(typed: string, canonical: string): string {
  const num = extractHouseNumber(typed);
  if (!num) return canonical;
  const parts = canonical.split(',').map((s) => s.trim()).filter(Boolean);
  const street = parts[0];
  if (!street) return typed.trim();
  // We only ever inject into the street segment, so only IT can hold a
  // duplicate: a numbered hit ("Aleea Zăvideni 3") or a street whose name is a
  // number ("1 Mai" / "13 Septembrie"). A digit anywhere else in the label —
  // "Sector 3", a postcode — is unrelated and must NOT block the house number.
  if (street.split(/\s+/).includes(num)) return canonical;
  parts[0] = `${street} nr. ${num}`;
  return parts.join(', ');
}
